import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { FileVideo, Plus, Loader2, Trash2, GraduationCap, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { buildFileUrl, uploadFile } from '../lib/files';

const initials = (n = '') => n.split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || 'T';

const ClipCard = ({ clip, currentUser, onDelete }) => {
  const url = buildFileUrl(clip.file.url || clip.file.id);
  const isVideo = clip.file.content_type?.startsWith('video/');
  const canDelete = currentUser?.id === clip.trainer_id || currentUser?.role === 'ADMIN';
  return (
    <Card className='border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden flex flex-col' data-testid={`training-clip-${clip.id}`}>
      <div className='aspect-video bg-slate-100 dark:bg-slate-950 relative'>
        {isVideo ? (
          <video src={url} controls preload='metadata' className='w-full h-full object-contain bg-black' data-testid={`clip-video-${clip.id}`} />
        ) : (
          <img src={url} alt={clip.title || 'training clip'} className='w-full h-full object-cover' />
        )}
      </div>
      <div className='p-3.5 flex-1 flex flex-col'>
        <div className='flex items-center gap-2 mb-1.5'>
          <Badge className='bg-[#1B3A87]/15 text-[#1B3A87] dark:text-[#93C5FD] border-0 text-[10px] tracking-wider'>
            <GraduationCap className='w-3 h-3 mr-1' /> Training Clip
          </Badge>
          {canDelete && (
            <button
              className='ml-auto text-slate-400 hover:text-[#B42318]'
              onClick={() => { if (window.confirm('Delete this clip?')) onDelete(clip); }}
              data-testid={`clip-delete-${clip.id}`}
              title='Delete clip'
            >
              <Trash2 className='w-3.5 h-3.5' />
            </button>
          )}
        </div>
        {clip.title && <div className='font-display font-bold text-sm text-slate-900 dark:text-slate-50'>{clip.title}</div>}
        {clip.caption && <div className='text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-3'>{clip.caption}</div>}
        <div className='mt-3 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400'>
          <Avatar className='w-5 h-5'><AvatarFallback className='bg-[#1B3A87] text-white text-[9px]'>{initials(clip.trainer_name)}</AvatarFallback></Avatar>
          <span className='truncate'>{clip.trainer_name}</span>
          <span>·</span>
          <span>{new Date(clip.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </Card>
  );
};

const UploadDialog = ({ open, onClose, dog, onCreated }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Pick a video or image first'); return; }
    setUploading(true);
    try {
      const uploaded = await uploadFile(file, 'training_clip', api);
      const form = new FormData();
      form.append('file_id', uploaded.id);
      if (title) form.append('title', title);
      if (caption) form.append('caption', caption);
      const { data } = await api.post(`/dogs/${dog.id}/clips`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Clip uploaded');
      onCreated(data);
      setFile(null); setTitle(''); setCaption('');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-md' data-testid='clip-upload-dialog'>
        <DialogHeader><DialogTitle className='flex items-center gap-2'><FileVideo className='w-5 h-5 text-[#1B3A87]' /> Upload training clip · {dog.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className='space-y-3'>
          <div>
            <label className='text-sm font-medium text-slate-700 dark:text-slate-200'>Video or photo *</label>
            <input
              ref={fileRef}
              type='file'
              accept='image/*,video/mp4,video/webm,video/quicktime'
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && f.size > 75 * 1024 * 1024) { toast.error('Max 75 MB'); return; }
                setFile(f || null);
              }}
              className='block mt-1.5 w-full text-sm text-slate-600 dark:text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-slate-200 dark:file:border-slate-700 file:text-sm file:font-medium file:bg-slate-50 dark:file:bg-slate-800 file:text-slate-700 dark:file:text-slate-200'
              data-testid='clip-upload-file'
              required
            />
            {file && <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>{file.name} · {(file.size/1024/1024).toFixed(2)} MB</div>}
          </div>
          <div>
            <label className='text-sm font-medium text-slate-700 dark:text-slate-200'>Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. Heel work — Day 5' data-testid='clip-upload-title' />
          </div>
          <div>
            <label className='text-sm font-medium text-slate-700 dark:text-slate-200'>Caption</label>
            <Textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder='Notes for the owner…' data-testid='clip-upload-caption' />
          </div>
          <DialogFooter>
            <Button type='submit' disabled={uploading || !file} className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='clip-upload-submit'>
              {uploading ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : null} {uploading ? 'Uploading…' : 'Publish clip'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DogClipGallery = ({ dog }) => {
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/dogs/${dog.id}/clips`);
      setClips(data);
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dog.id]);

  const canUpload =
    user?.role === 'ADMIN' ||
    (user?.role === 'TRAINER' && (!dog.assigned_trainer_id || dog.assigned_trainer_id === user.id));

  const onDelete = async (clip) => {
    try {
      await api.delete(`/dogs/${dog.id}/clips/${clip.id}`);
      setClips((arr) => arr.filter((c) => c.id !== clip.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className='border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5' data-testid='dog-clip-gallery'>
      <div className='flex items-center justify-between mb-4 flex-wrap gap-2'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Training Clips</div>
          <h3 className='font-display text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mt-0.5 flex items-center gap-2'>
            <FileVideo className='w-5 h-5 text-[#1B3A87] dark:text-[#93C5FD]' /> {dog.name}'s Progress Reel
          </h3>
        </div>
        {canUpload && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className='bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='upload-clip-button'>
                <Plus className='w-4 h-4 mr-1.5' /> Upload clip
              </Button>
            </DialogTrigger>
            <UploadDialog open={open} onClose={() => setOpen(false)} dog={dog} onCreated={(c) => setClips((arr) => [c, ...arr])} />
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className='py-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2'><Loader2 className='w-4 h-4 animate-spin' /> Loading clips…</div>
      ) : clips.length === 0 ? (
        <div className='py-10 text-center text-slate-500 dark:text-slate-400 text-sm'>
          <FileVideo className='w-10 h-10 mx-auto text-slate-300 dark:text-slate-700 mb-2' />
          No clips yet.
          {canUpload && <div className='mt-2 text-xs'>Upload a short video to share progress with the owner.</div>}
          {!canUpload && dog.assigned_trainer_id && (
            <div className='mt-2 text-xs flex items-center justify-center gap-1.5'>
              <MessageSquare className='w-3.5 h-3.5' /> Your trainer will post clips here as your dog progresses.
            </div>
          )}
        </div>
      ) : (
        <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-4'>
          {clips.map((clip) => (
            <ClipCard key={clip.id} clip={clip} currentUser={user} onDelete={onDelete} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default DogClipGallery;
